/**
 * 「既存地点から路線を作成」(`RouteReportModal`) 専用の小型プレビュー地図 (2026-07-23 追加)。
 *
 * `AreaReportPreviewMap.tsx` と同じ構造 (地点はドーナツ表示＋近接クラスタのスパイダーファイ、
 * 自前の縮尺バー) を踏襲するが、選択領域が「多角形」ではなく「描いた路線 (折れ線)」である点が
 * 異なるため別ファイルとして実装する。本コンポーネントは画面表示専用で、`AreaReportPreviewMap`
 * が持つ PDF エクスポート用の `capture()` は不要（`RouteReportModal` はPDF出力をスコープ外としている
 * ため、docs/25 §10-6 参照）。
 */
import 'maplibre-gl/dist/maplibre-gl.css';
import maplibregl, { type Map as MaplibreMap } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { buildDonutSvgMarkup, type DonutSegment } from './streetSurveyClusterDonut';

const LINE_SOURCE_ID = 'route-report-line';
const LINE_LAYER_ID = 'route-report-line-stroke';
const LEADER_SOURCE_ID = 'route-report-leader-lines';
const LEADER_LAYER_ID = 'route-report-leader-lines-stroke';
const MARKER_SIZE = 32;
/** これより画面上で近い (px) 地点は重なるとみなしクラスタ化する。 */
const CLUSTER_THRESHOLD_PX = MARKER_SIZE + 8;
/** 縮尺バーの最大幅 (px)。この幅に収まる「きりのいい距離」を探す。 */
const SCALE_BAR_MAX_WIDTH_PX = 100;

export interface RouteReportPreviewMapPoint {
  id: string;
  lat: number;
  lng: number;
  /** 「検出された地点一覧」と対応する1始まりの番号。 */
  number: number;
  segments: DonutSegment[];
}

/** スパイダーファイ後の表示用地点 (真の座標 + 表示座標 + オフセットの有無)。 */
interface PlacedPoint extends RouteReportPreviewMapPoint {
  displayLat: number;
  displayLng: number;
  offset: boolean;
}

export interface RouteReportPreviewMapProps {
  points: RouteReportPreviewMapPoint[];
  /** 描いた路線の頂点 (`StreetSurveyResultsMap` の onRouteDrawn 経由)。 */
  lineCoordinates: { latitude: number; longitude: number }[];
  className?: string;
}

function haversineMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** 距離を 1/2/3/5/10 系列の「きりのいい数値」に丸める (MapLibre/Mapbox GL JS 本家 ScaleControl と同じ式)。 */
function roundNiceDistance(num: number): number {
  const pow10 = Math.pow(10, `${Math.floor(num)}`.length - 1);
  const d = num / pow10;
  const step = d >= 10 ? 10 : d >= 5 ? 5 : d >= 3 ? 3 : d >= 2 ? 2 : 1;
  return pow10 * step;
}

interface ScaleBarInfo {
  widthPx: number;
  label: string;
}

/** 現在の地図中心緯度における画面px→実距離の換算から、縮尺バーの幅とラベルを算出する。 */
function computeScaleBar(
  map: MaplibreMap,
  maxWidthPx = SCALE_BAR_MAX_WIDTH_PX,
): ScaleBarInfo | null {
  const container = map.getContainer();
  const y = container.clientHeight / 2;
  const left = map.unproject([0, y]);
  const right = map.unproject([maxWidthPx, y]);
  const maxMeters = haversineMeters(
    { lat: left.lat, lng: left.lng },
    { lat: right.lat, lng: right.lng },
  );
  if (!Number.isFinite(maxMeters) || maxMeters <= 0) return null;
  const niceMeters = roundNiceDistance(maxMeters);
  const widthPx = maxWidthPx * (niceMeters / maxMeters);
  const label =
    niceMeters >= 1000 ? `${Math.round(niceMeters / 100) / 10}km` : `${Math.round(niceMeters)}m`;
  return { widthPx, label };
}

export function RouteReportPreviewMap({
  points,
  lineCoordinates,
  className,
}: RouteReportPreviewMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const [ready, setReady] = useState(false);
  const [scaleBar, setScaleBar] = useState<ScaleBarInfo | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          gsi: {
            type: 'raster',
            tiles: ['https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'],
            tileSize: 256,
            maxzoom: 18,
            attribution: '地理院タイル',
          },
        },
        layers: [{ id: 'gsi', type: 'raster', source: 'gsi' }],
      },
      center: [139.767, 35.681],
      zoom: 12,
      attributionControl: false,
    });
    map.on('load', () => setReady(true));
    mapRef.current = map;
    const markers = markersRef.current;
    return () => {
      markers.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
      setReady(false);
    };
  }, []);

  // 縮尺バー — ズーム/パンのたび再計算。
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const update = () => setScaleBar(computeScaleBar(map));
    update();
    map.on('move', update);
    map.on('resize', update);
    return () => {
      map.off('move', update);
      map.off('resize', update);
    };
  }, [ready]);

  // 路線 (折れ線) — ネイティブレイヤー、地図で線を描くモードと同じ配色。
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const lineFc: GeoJSON.FeatureCollection =
      lineCoordinates.length >= 2
        ? {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: lineCoordinates.map((c) => [c.longitude, c.latitude]),
                },
              },
            ],
          }
        : { type: 'FeatureCollection', features: [] };

    if (!map.getSource(LINE_SOURCE_ID)) {
      map.addSource(LINE_SOURCE_ID, { type: 'geojson', data: lineFc });
      map.addLayer({
        id: LINE_LAYER_ID,
        type: 'line',
        source: LINE_SOURCE_ID,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#0EA5E9', 'line-width': 3 },
      });
    } else {
      (map.getSource(LINE_SOURCE_ID) as maplibregl.GeoJSONSource).setData(lineFc);
    }
  }, [ready, lineCoordinates]);

  // 地点 — 結果地図と同じドーナツ DOM Marker + 中央に番号 + 近接クラスタのスパイダーファイ。
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const bounds = new maplibregl.LngLatBounds();
    let hasPoint = false;
    for (const p of points) {
      if (p.lat === 0 && p.lng === 0) continue;
      bounds.extend([p.lng, p.lat]);
      hasPoint = true;
    }
    for (const c of lineCoordinates) {
      bounds.extend([c.longitude, c.latitude]);
      hasPoint = true;
    }
    if (hasPoint) {
      map.fitBounds(bounds, { padding: 40, maxZoom: 18, duration: 0 });
    }

    // fitBounds (duration:0) の反映を1tick待ってから、確定した画面座標でスパイダーファイする。
    const t = window.setTimeout(() => {
      try {
        map.resize();
      } catch {
        /* ignore */
      }

      markersRef.current.forEach((m) => m.remove());
      const placed = declutterPoints(map, points);

      markersRef.current = placed.map((p) => {
        const padSize = MARKER_SIZE + 6;
        const el = document.createElement('div');
        el.style.cssText = `width:${padSize}px;height:${padSize}px;border-radius:50%;background:white;border:1.5px solid rgba(15,23,42,0.2);box-shadow:0 1px 4px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;`;
        const donutWrap = document.createElement('div');
        donutWrap.style.cssText = `width:${MARKER_SIZE}px;height:${MARKER_SIZE}px;`;
        donutWrap.innerHTML = buildDonutSvgMarkup(
          p.segments,
          MARKER_SIZE,
          Math.max(4, MARKER_SIZE * 0.32),
          '#94A3B8',
        );
        el.appendChild(donutWrap);
        const label = document.createElement('span');
        label.textContent = String(p.number);
        label.style.cssText = `position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:${MARKER_SIZE * 0.34}px;font-weight:700;color:#0f172a;line-height:1;`;
        el.appendChild(label);
        return new maplibregl.Marker({ element: el })
          .setLngLat([p.displayLng, p.displayLat])
          .addTo(map);
      });

      // 吹き出し線: オフセットした地点だけ、真の位置 → 表示位置を細線で結ぶ。
      const leaderFc: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: placed
          .filter((p) => p.offset)
          .map((p) => ({
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: [
                [p.lng, p.lat],
                [p.displayLng, p.displayLat],
              ],
            },
          })),
      };
      if (!map.getSource(LEADER_SOURCE_ID)) {
        map.addSource(LEADER_SOURCE_ID, { type: 'geojson', data: leaderFc });
        map.addLayer({
          id: LEADER_LAYER_ID,
          type: 'line',
          source: LEADER_SOURCE_ID,
          paint: { 'line-color': '#64748b', 'line-width': 1.5, 'line-dasharray': [2, 1.5] },
        });
      } else {
        (map.getSource(LEADER_SOURCE_ID) as maplibregl.GeoJSONSource).setData(leaderFc);
      }
    }, 80);

    return () => window.clearTimeout(t);
  }, [ready, points, lineCoordinates]);

  return (
    <div className="relative">
      <div
        ref={containerRef}
        className={className ?? 'w-full h-56 rounded border border-slate-200'}
        data-testid="route-report-preview-map"
      />
      {scaleBar && (
        <div className="absolute bottom-2 right-2 bg-white/75 rounded px-1.5 pt-0.5 pb-1 pointer-events-none">
          <div className="text-[10px] text-slate-700 text-center leading-none mb-0.5">
            {scaleBar.label}
          </div>
          <div
            className="border-b-2 border-l-2 border-r-2 border-slate-600 h-1"
            style={{ width: `${scaleBar.widthPx}px` }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * 画面座標が近い地点をクラスタ化し、2点以上のクラスタは中心から円状に均等配置した
 * 仮想位置 (displayLat/displayLng) を割り当てる (スパイダーファイ)。1点だけのクラスタは
 * 真の位置のまま (`offset:false`)。`AreaReportPreviewMap.tsx` の同名関数と同一ロジック。
 */
function declutterPoints(map: MaplibreMap, points: RouteReportPreviewMapPoint[]): PlacedPoint[] {
  const valid = points.filter((p) => !(p.lat === 0 && p.lng === 0));
  const projected = valid.map((p) => ({ p, screen: map.project([p.lng, p.lat]) }));

  const groups: { items: typeof projected }[] = [];
  for (const item of projected) {
    const group = groups.find((g) => {
      const cx = g.items.reduce((s, i) => s + i.screen.x, 0) / g.items.length;
      const cy = g.items.reduce((s, i) => s + i.screen.y, 0) / g.items.length;
      return Math.hypot(item.screen.x - cx, item.screen.y - cy) < CLUSTER_THRESHOLD_PX;
    });
    if (group) group.items.push(item);
    else groups.push({ items: [item] });
  }

  const result: PlacedPoint[] = [];
  for (const g of groups) {
    if (g.items.length === 1) {
      const { p } = g.items[0]!;
      result.push({ ...p, displayLat: p.lat, displayLng: p.lng, offset: false });
      continue;
    }
    const n = g.items.length;
    const cx = g.items.reduce((s, i) => s + i.screen.x, 0) / n;
    const cy = g.items.reduce((s, i) => s + i.screen.y, 0) / n;
    // 隣接する地点同士が重ならない半径 (円周上に n 点を等間隔配置する弦長の式から逆算)。
    const radius = Math.max(MARKER_SIZE, CLUSTER_THRESHOLD_PX / (2 * Math.sin(Math.PI / n)) + 4);
    g.items.forEach((item, i) => {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2;
      const ox = cx + radius * Math.cos(angle);
      const oy = cy + radius * Math.sin(angle);
      const lngLat = map.unproject([ox, oy]);
      result.push({ ...item.p, displayLat: lngLat.lat, displayLng: lngLat.lng, offset: true });
    });
  }
  return result;
}
